import { INVOICE_AMOUNT_EPS } from '@/lib/invoice-utils';
import { getHeadlinePayableDueForDashboard } from '@/lib/invoice-payment-breakdown';
import { isInviteeCoveredByInviter } from '@/lib/event-utils';
import type { Event, EventStatus, Invoice, RSVP } from '@/types';

/**
 * Aligns with AttendanceListPage payment display: treat as paid when the invoice
 * is settled (or zero total) even if the RSVP paid flag was not synced, and
 * still respect an outstanding balance over the RSVP paid flag.
 * When `invoiceBreakdown` is set, headline due matches PaymentPage (live ticket + booking cap).
 */
export function isRsvpEffectivelyPaidForDashboard(
  rsvp: RSVP,
  invoice: Invoice | undefined,
  invoiceBreakdown?: { event: Event; rsvps: RSVP[] } | null,
): boolean {
  if (isInviteeCoveredByInviter(rsvp)) {
    return true;
  }

  if (invoice?.status === 'voided') {
    return rsvp.paid === true;
  }

  if (rsvp.paid === true) {
    if (invoice && getHeadlinePayableDueForDashboard(invoice, invoiceBreakdown ?? null) > INVOICE_AMOUNT_EPS) {
      return false;
    }

    return true;
  }

  if (!invoice) {
    return false;
  }

  const ticketDue = getHeadlinePayableDueForDashboard(invoice, invoiceBreakdown ?? null);
  const total = invoice.totalAmount ?? 0;

  return ticketDue <= INVOICE_AMOUNT_EPS || total === 0;
}

export const PERSONAL_DASHBOARD_PAYMENT_LEAKING_STATUSES = [
  'Paid',
  'Pending Payment',
  'Payment Required',
  'Pending Verification',
] as const;

const PAYMENT_LEAKING_SET = new Set<string>(PERSONAL_DASHBOARD_PAYMENT_LEAKING_STATUSES);

export type PersonalDashboardStatusVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'warning'
  | 'outline';

/**
 * Host-paid invitees must not infer settlement; replace payment-facing statuses with "Host Pays".
 */
export function personalDashboardStatusForHostPaidInvitee(
  inviterCoversGuest: boolean,
  status: string,
  variant: PersonalDashboardStatusVariant,
): { status: string; variant: PersonalDashboardStatusVariant } {
  if (inviterCoversGuest && PAYMENT_LEAKING_SET.has(status)) {
    return { status: 'Host Pays', variant: 'secondary' };
  }
  return { status, variant };
}

export type PersonalDashboardRsvpRow = RSVP & {
  eventName?: string;
  eventDate?: Date;
  unitId?: string;
  unitName?: string;
  lodgeId?: string;
  lodgeName?: string;
  eventStatus?: EventStatus;
};

/**
 * Status badge label and variant for the dining personal dashboard "My RSVP's" list.
 */
export function getPersonalDashboardRsvpStatus(
  rsvp: PersonalDashboardRsvpRow,
  eventDate: Date | null,
  invoice?: Invoice,
  invoiceBreakdown?: { event: Event; rsvps: RSVP[] } | null,
): { status: string; variant: PersonalDashboardStatusVariant } {
  if (rsvp.apologies === true || rsvp.status === 'declined') {
    return { status: 'Apologies', variant: 'secondary' };
  }

  if (rsvp.status === 'cancelled') {
    return { status: 'Cancelled', variant: 'destructive' };
  }

  const now = new Date();
  const isPast = eventDate ? eventDate <= now : rsvp.eventStatus === 'completed' || rsvp.eventStatus === 'closed';
  const inviterCoversGuest = isInviteeCoveredByInviter(rsvp);
  const ticketAmountDue =
    invoice != null && invoice.status !== 'voided'
      ? getHeadlinePayableDueForDashboard(invoice, invoiceBreakdown ?? null)
      : 0;
  const hasOutstandingBalance =
    !inviterCoversGuest &&
    invoice != null &&
    invoice.status !== 'voided' &&
    ticketAmountDue > INVOICE_AMOUNT_EPS;
  const isPaid = isRsvpEffectivelyPaidForDashboard(rsvp, invoice, invoiceBreakdown ?? null);

  const hasMealChoice = !!(
    rsvp.responses?.Meal ||
    (rsvp.responses && Object.keys(rsvp.responses).some((key) => key !== 'Allergies'))
  );

  let raw: { status: string; variant: PersonalDashboardStatusVariant };

  if (rsvp.status === 'waitlist' && rsvp.eventStatus !== 'published') {
    raw = { status: 'On Waitlist', variant: 'secondary' };
  } else if (rsvp.status === 'waitlist' && rsvp.eventStatus === 'published' && !hasMealChoice) {
    raw = { status: 'Awaiting Meal Choice', variant: 'warning' };
  } else if (invoice?.status === 'pending_verification') {
    raw = { status: 'Pending Verification', variant: 'warning' };
  } else if (rsvp.status === 'pending') {
    raw = { status: 'Pending', variant: 'secondary' };
  } else if (isPast && !isPaid) {
    raw = { status: 'Payment Required', variant: 'destructive' };
  } else if (hasOutstandingBalance) {
    raw = { status: 'Pending Payment', variant: 'destructive' };
  } else if (isPast && isPaid) {
    raw = { status: 'Finished', variant: 'outline' };
  } else if (isPaid && !isPast) {
    raw = { status: 'Paid', variant: 'default' };
  } else if (rsvp.status === 'confirmed') {
    raw = { status: 'Paid', variant: 'default' };
  } else {
    raw = { status: 'Pending', variant: 'secondary' };
  }

  return personalDashboardStatusForHostPaidInvitee(inviterCoversGuest, raw.status, raw.variant);
}
